import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogTextButton } from "@/components/common/ActionDialog";
import { paymentMethodsService, type PaymentMethod } from "@/services/paymentMethods.service";
import { countFilledMethods } from "@/utils/payoutMethods";
import { cn } from "@/lib/utils";

export interface PaymentMethodFieldValue {
  fieldName: string;
  value: string;
}

export interface PaymentMethodData {
  paymentMethodId: string;
  fieldValues: PaymentMethodFieldValue[];
}

interface PaymentInfoFormProps {
  paymentMethodsData?: PaymentMethodData[];
  onChangePaymentMethodsData?: (data: PaymentMethodData[]) => void;
  /** The methods to show; when omitted the form fetches the active ones. */
  methods?: PaymentMethod[];
  showTitle?: boolean;
  showSaveButton?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string;
  /** Names of methods the paying brand supports, noted beside the field. */
  highlightedMethods?: string[];
  /** When set, only these method names are rendered. */
  filterMethods?: string[];
  /** Minimum filled methods before Save enables. One is enough to be paid. */
  minMethods?: number;
  saveLabel?: string;
}

const PLACEHOLDER: Record<string, string> = {
  paypal: "name@example.com",
  cashapp: "$cashtag",
  venmo: "@username",
  zelle: "+1 555 123 4567",
  remitly: "Account number",
  "e-transfer": "name@example.com",
  litecoin: "Wallet address",
  bitcoin: "Wallet address",
  ethereum: "Wallet address",
  solana: "Wallet address",
  wise: "name@example.com",
};

function placeholderFor(methodName: string, fieldName: string) {
  const byMethod = PLACEHOLDER[methodName.toLowerCase()];
  if (byMethod) return byMethod;
  if (fieldName === "email") return "name@example.com";
  if (fieldName === "recipient_address") return "Wallet address";
  if (fieldName === "cash_tag") return "$cashtag";
  return "";
}

/**
 * One row per method: the name, then the field a brand needs to pay
 * through it. Used on the affiliate's settings, the payout request and the
 * agency's payment details, so it stays a plain list of fields.
 */
export function PaymentInfoForm({
  paymentMethodsData = [],
  onChangePaymentMethodsData,
  methods,
  showTitle = true,
  showSaveButton = false,
  onSave,
  onCancel,
  isLoading = false,
  error,
  highlightedMethods = [],
  filterMethods,
  minMethods = 1,
  saveLabel = "Save changes",
}: PaymentInfoFormProps) {
  const [fetched, setFetched] = useState<PaymentMethod[] | null>(null);
  const [fetching, setFetching] = useState(!methods);

  useEffect(() => {
    if (methods) return;
    let cancelled = false;
    (async () => {
      setFetching(true);
      try {
        const all = await paymentMethodsService.getAllPaymentMethods();
        if (!cancelled) setFetched(all.filter((m) => m.isActive));
      } catch {
        if (!cancelled) setFetched([]);
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [methods]);

  const available = methods ?? fetched ?? [];
  const visible = filterMethods && filterMethods.length > 0 ? available.filter((m) => filterMethods.includes(m.name)) : available;
  const filledCount = countFilledMethods(paymentMethodsData);

  const valueOf = (methodId: string, fieldName: string) =>
    paymentMethodsData.find((m) => m.paymentMethodId === methodId)?.fieldValues.find((f) => f.fieldName === fieldName)?.value ?? "";

  const setValue = (methodId: string, fieldName: string, value: string) => {
    if (!onChangePaymentMethodsData) return;
    const next = paymentMethodsData.map((m) => ({ ...m, fieldValues: m.fieldValues.map((f) => ({ ...f })) }));
    const idx = next.findIndex((m) => m.paymentMethodId === methodId);
    if (!value.trim()) {
      if (idx === -1) return;
      const rest = next[idx].fieldValues.filter((f) => f.fieldName !== fieldName);
      if (rest.every((f) => !f.value || !f.value.trim())) next.splice(idx, 1);
      else next[idx].fieldValues = rest;
    } else if (idx === -1) {
      next.push({ paymentMethodId: methodId, fieldValues: [{ fieldName, value }] });
    } else {
      const f = next[idx].fieldValues.find((x) => x.fieldName === fieldName);
      if (f) f.value = value;
      else next[idx].fieldValues.push({ fieldName, value });
    }
    onChangePaymentMethodsData(next);
  };

  return (
    <div className="space-y-4">
      {showTitle && <h3 className="text-[15px] font-semibold text-foreground">Payment methods</h3>}

      {fetching && !methods ? (
        <div className="space-y-4 animate-pulse">
          {[0, 1, 2].map((i) => (
            <div key={i} className="grid grid-cols-[7rem_1fr] items-center gap-3">
              <div className="h-3.5 w-16 rounded bg-muted" />
              <div className="h-9 w-full rounded-lg bg-muted" />
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">No payment methods are available right now</p>
      ) : (
        <div className="space-y-4">
          {visible.map((method) => {
            const field = method.fields[0];
            if (!field) return null;
            const id = `payment-method-${method.id}`;
            const supported = highlightedMethods.includes(method.name);
            return (
              <div key={method.id} className="grid gap-1.5 sm:grid-cols-[7rem_1fr] sm:items-center sm:gap-3">
                <Label htmlFor={id} className={cn("text-foreground", supported && "font-semibold")}>
                  {method.name}
                </Label>
                <div className="min-w-0">
                  <Input
                    id={id}
                    type={field.fieldName === "email" ? "email" : "text"}
                    inputMode={field.fieldName === "email" ? "email" : "text"}
                    value={valueOf(method.id, field.fieldName)}
                    onChange={(e) => setValue(method.id, field.fieldName, e.target.value)}
                    placeholder={placeholderFor(method.name, field.fieldName)}
                    aria-label={field.label}
                    autoComplete="off"
                  />
                  {supported && <p className="mt-1 text-xs text-muted-foreground">This brand pays through {method.name}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        filledCount < minMethods &&
        !fetching && (
          <p className="text-xs text-muted-foreground">
            {minMethods <= 1 ? "One method is enough to get paid" : `Fill in at least ${minMethods} methods`}
          </p>
        )
      )}

      {showSaveButton && (
        <div className="flex items-center justify-end gap-4 border-t border-border pt-3">
          <DialogTextButton onClick={onCancel} disabled={isLoading}>
            Cancel
          </DialogTextButton>
          <Button type="button" onClick={onSave} disabled={isLoading || filledCount < minMethods}>
            {saveLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
